/**
 * Validate No File Import Cycles Executor
 *
 * Per-project circular-dependency gate. Runs `madge` over the project's
 * TypeScript sources and fails when an import cycle is found.
 *
 * Unlike the old `nx:run-commands` target (which shelled out to a runtime
 * `npx madge` fetch — undeclared, so consumers of the published plugin had no
 * local madge and CI runners corrupted their npx cache fetching it), this executor:
 *   - invokes the madge it bundles as a dependency (deterministic, no network),
 *   - is driven by webpieces.config.json like every other webpieces rule, so it
 *     supports an on/off `mode` and a time-boxed `turnOffRuleUntilEpoch`.
 *
 * Config (webpieces.config.json, rule key `no-file-import-cycles`):
 *   "no-file-import-cycles": {
 *       "mode": "RUN_EVERY_TIME",           // "OFF" disables the gate everywhere
 *       "turnOffRuleUntilEpoch": 1771931925,  // epoch SECONDS; while now < epoch,
 *                                            //   cycles are reported but the gate PASSES
 *                                            //   (warn, don't fail). After it, fails again.
 *       "turnOffRuleWhileOnBranch": null,   // branch name; while that branch is checked out,
 *                                            //   cycles are reported but the gate PASSES
 *       "ignoreTypeOnly": true,             // ignore `import type` re-export cycles
 *                                            //   (erased at compile time, harmless at runtime)
 *       "excludePackages": ["@db/entities"] // npm package names whose source trees madge
 *                                            //   should NOT traverse (stops foreign cycles
 *                                            //   from leaking into this project's report)
 *   }
 *
 * Mirrors the dated-disable model already used for the method/file-size rules:
 * the epoch is a grace window so a strict gate can be turned on against an
 * existing codebase without an open-ended "off everywhere" escape hatch.
 *
 * Usage: nx run <project>:validate-no-file-import-cycles
 */

import type { ExecutorContext } from '@nx/devkit';
import { loadAndValidate, shouldSkipRule } from '@webpieces/rules-config';
import * as fs from 'fs';
import * as path from 'path';
import { toError } from '../../toError';

export type ValidateNoFileImportCyclesMode = 'RUN_EVERY_TIME' | 'OFF';

export interface ValidateNoFileImportCyclesOptions {
    // No options here — config comes from webpieces.config.json at runtime.
}

export interface ExecutorResult {
    success: boolean;
}

const RULE_NAME = 'no-file-import-cycles';

// madge ships no type declarations; describe the slice of its API we use.
// webpieces-disable no-any-unknown -- minimal hand-typed surface for an untyped dependency
interface MadgeOptions {
    fileExtensions: string[];
    excludeRegExp?: string[];
    detectiveOptions?: Record<string, unknown>;
}
interface MadgeInstance {
    circular(): string[][];
    // The full dependency graph: id → its dependency ids. Keys are the ids madge
    // traversed (relative to the base it was invoked with) — used to detect
    // excludeRegExp patterns that match nothing.
    obj(): Record<string, string[]>;
}
type MadgeFn = (target: string, options: MadgeOptions) => Promise<MadgeInstance>;

// madge's CJS export is the callable itself; some bundlers wrap it under `.default`.
interface MadgeModuleExtras {
    default?: MadgeFn;
}
type MadgeModule = MadgeFn & MadgeModuleExtras;

function loadMadge(): MadgeFn {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod: MadgeModule = require('madge');
    return mod.default ?? mod;
}

/**
 * Decide whether the gate should still FAIL on cycles (true) or only warn
 * (false), considering the universal escape hatches: the turnOffRuleUntilEpoch
 * grace window and turnOffRuleWhileOnBranch. Logs a one-line explanation when a
 * hatch is active.
 */
function isFailingActive(epoch: number | undefined, branch: string | undefined): boolean {
    const skip = shouldSkipRule(epoch, branch);
    if (skip.skip) {
        console.log(
            `\n⏳ no-file-import-cycles: ${skip.reason}.` +
                '\n   Cycles will be reported but NOT fail the build.\n',
        );
        return false;
    }
    return true;
}

// Never scan build output or declaration files. A project that compiles into a
// local `dist/` (or build/out/coverage) would otherwise report cycles among the
// emitted `*.d.ts` files instead of — or in addition to — the real source
// cycles, so the gate would flag compiled-output noise and could diverge from a
// plain `madge src` run. Excluding these makes the gate scan source only.
const EXCLUDE_BUILD_DIRS = '(^|/)(node_modules|dist|build|out|coverage|\\.nx|\\.next)(/|$)';
const EXCLUDE_DECLARATION_FILES = '\\.d\\.ts$';

function escapeRegex(s: string): string {
    return s.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&');
}

class TsconfigCompilerOptions {
    paths?: Record<string, string[]>;
}
class TsconfigBase {
    compilerOptions?: TsconfigCompilerOptions;
}

/** Read tsconfig.base.json compilerOptions.paths from the workspace root, or null on failure. */
function readTsconfigPaths(workspaceRoot: string): Record<string, string[]> | null {
    // eslint-disable-next-line @webpieces/no-unmanaged-exceptions -- best-effort read; null on any failure
    try {
        const tsconfigPath = path.join(workspaceRoot, 'tsconfig.base.json');
        const content = fs.readFileSync(tsconfigPath, 'utf8');
        const tsconfig = JSON.parse(content) as TsconfigBase;
        return tsconfig?.compilerOptions?.paths ?? null;
    // webpieces-disable catch-error-pattern -- file missing or malformed JSON; caller handles null
    } catch (err: unknown) {
        //const error = toError(err);
        return null;
    }
}

/**
 * Walk up from startPath to find the nearest ancestor directory that contains
 * a package.json. Returns that directory path, or null if none found.
 */
function findPackageRoot(startPath: string): string | null {
    let dir = fs.statSync(startPath).isDirectory() ? startPath : path.dirname(startPath);
    const fsRoot = path.parse(dir).root;
    while (dir !== fsRoot) {
        if (fs.existsSync(path.join(dir, 'package.json'))) return dir;
        dir = path.dirname(dir);
    }
    return null;
}

function resolvePackageDir(pkgName: string, workspaceRoot: string): string | null {
    // First try require.resolve (works for installed / symlinked packages).
    // eslint-disable-next-line @webpieces/no-unmanaged-exceptions -- resolution failure is expected; fall through to tsconfig path lookup
    try {
        const pkgJson = require.resolve(`${pkgName}/package.json`, { paths: [workspaceRoot] });
        return fs.realpathSync(path.dirname(pkgJson));
    // webpieces-disable catch-error-pattern -- expected for non-installed packages; fall through to tsconfig path lookup
    } catch (err: unknown) {
        //const error = toError(err);
        // Fall through to tsconfig path resolution for pnpm workspace packages.
    }

    // Fallback: resolve via tsconfig.base.json compilerOptions.paths.
    // pnpm workspace packages are not in node_modules, so require.resolve fails above;
    // tsconfig.base.json maps e.g. "@acme-internal/db" → ["libraries/db/index.ts"].
    const tsconfigPaths = readTsconfigPaths(workspaceRoot);
    const entries = tsconfigPaths?.[pkgName];
    if (!entries || entries.length === 0) {
        console.warn(
            `⚠️  no-file-import-cycles: could not resolve excludePackages entry "${pkgName}"` +
                ` — not found in node_modules or tsconfig.base.json paths. Skipping.`,
        );
        return null;
    }
    // eslint-disable-next-line @webpieces/no-unmanaged-exceptions -- best-effort fallback; warn on any failure
    try {
        const resolved = path.resolve(workspaceRoot, entries[0]);
        const pkgRoot = findPackageRoot(resolved);
        if (!pkgRoot) {
            console.warn(
                `⚠️  no-file-import-cycles: resolved "${pkgName}" → "${resolved}"` +
                    ` but found no package.json in parent directories — skipping.`,
            );
            return null;
        }
        return pkgRoot;
    } catch (err: unknown) {
        const error = toError(err);
        console.warn(
            `⚠️  no-file-import-cycles: could not resolve excludePackages entry "${pkgName}"` +
                ` via tsconfig paths (${error.message}) — skipping.`,
        );
        return null;
    }
}

/**
 * Realpath a directory, tolerating a non-existent path (returns the input).
 * madge traverses through pnpm symlinks to real paths, and resolvePackageDir
 * already realpaths its result, so projectRoot must be realpath'd too or the
 * computed relative path won't line up with the ids madge emits.
 */
// webpieces-disable no-function-outside-class -- nx executor module; file is functional by convention (see resolvePackageDir/reportCycles above)
function realpathOrSelf(dir: string): string {
    // eslint-disable-next-line @webpieces/no-unmanaged-exceptions -- best-effort; fall back to the raw path
    try {
        return fs.realpathSync(dir);
    // webpieces-disable catch-error-pattern -- path may not exist yet; use it as-is
    } catch (err: unknown) {
        //const error = toError(err);
        return dir;
    }
}

/** True if dir contains at least one .ts/.tsx source file anywhere beneath it. */
// webpieces-disable no-function-outside-class -- nx executor module; file is functional by convention (see resolvePackageDir/reportCycles above)
function hasSourceFiles(dir: string): boolean {
    // eslint-disable-next-line @webpieces/no-unmanaged-exceptions -- best-effort scan; false on any read failure
    try {
        for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
            if (entry.name === 'node_modules' || entry.name === 'dist' || entry.name === 'build') continue;
            const full = path.join(dir, entry.name);
            if (entry.isDirectory()) {
                if (hasSourceFiles(full)) return true;
            } else if (/\.tsx?$/.test(entry.name) && !entry.name.endsWith('.d.ts')) {
                return true;
            }
        }
        return false;
    // webpieces-disable catch-error-pattern -- unreadable dir; treat as no source
    } catch (err: unknown) {
        //const error = toError(err);
        return false;
    }
}

/**
 * Build the exclude pattern for one resolved package dir, RELATIVE to the base
 * madge is invoked with (projectRoot). madge matches excludeRegExp against ids
 * relative to that base — e.g. '../../libraries/db/src/x.ts' — so an absolute
 * `^/abs/...` anchor can never match and silently excludes nothing. Returns the
 * relative-anchored pattern. Warns (but still returns the pattern) when the dir
 * holds no source madge would traverse, closing the resolves-but-matches-nothing
 * silent-failure gap.
 */
// webpieces-disable no-function-outside-class -- nx executor module; file is functional by convention (see resolvePackageDir/reportCycles above)
function buildExcludePattern(dir: string, base: string, pkgName: string): string {
    // madge ids always use forward slashes; normalise path.sep so this holds on Windows.
    const rel = path.relative(base, dir).split(path.sep).join('/');
    if (!hasSourceFiles(dir)) {
        console.warn(
            `⚠️  no-file-import-cycles: excludePackages entry "${pkgName}" resolved to "${dir}"` +
                ` but that directory contains no .ts/.tsx source — the exclusion will match nothing.`,
        );
    }
    return `^${escapeRegex(rel)}(/|$)`;
}

// webpieces-disable no-function-outside-class -- nx executor module; file is functional by convention (see resolvePackageDir/reportCycles above)
export function buildMadgeOptions(
    ignoreTypeOnly: boolean,
    excludePackages: string[],
    workspaceRoot: string,
    projectRoot: string,
    // Raw patterns from config, handed to madge verbatim. Matched against ids RELATIVE TO
    // projectRoot (see NoFileImportCyclesConfig.excludeRegExp) — the executor warns on any that
    // match nothing so a mis-anchored (workspace-rooted / absolute) pattern isn't a silent no-op.
    userExcludeRegExp: string[] = [],
): MadgeOptions {
    const excludeRegExp = [EXCLUDE_BUILD_DIRS, EXCLUDE_DECLARATION_FILES, ...userExcludeRegExp];
    // madge is invoked with projectRoot as its base and emits ids relative to it;
    // realpath it to match resolvePackageDir's realpath'd result under pnpm symlinks.
    const base = realpathOrSelf(projectRoot);
    for (const pkg of excludePackages) {
        const dir = resolvePackageDir(pkg, workspaceRoot);
        if (dir) excludeRegExp.push(buildExcludePattern(dir, base, pkg));
    }
    const options: MadgeOptions = {
        fileExtensions: ['ts', 'tsx'],
        excludeRegExp,
    };
    if (ignoreTypeOnly) {
        // dependency-tree's TS detective drops `import type {...}` edges with this flag.
        options.detectiveOptions = {
            ts: { skipTypeImports: true },
            tsx: { skipTypeImports: true },
        };
    }
    return options;
}

/**
 * The relative-id trap: madge matches excludeRegExp against ids RELATIVE TO
 * projectRoot (e.g. 'src/generated/api.ts'), so a workspace-anchored pattern
 * ('^libraries/apis/foo/src/...') or an absolute one ('^/abs/...') silently
 * matches nothing and the exclusion is a no-op. To make that visible, run madge
 * WITHOUT the user patterns to get the id universe and warn for any pattern that
 * matches none of it (or is not a valid regex). Same "resolves but matches
 * nothing" idea buildExcludePattern uses for excludePackages. Only invoked when
 * the consumer actually configured excludeRegExp, so the extra traversal is paid
 * only in the rare case that opts in.
 */
// webpieces-disable no-function-outside-class -- nx executor module; file is functional by convention (see resolvePackageDir/reportCycles above)
async function warnOnUnmatchedExcludeRegExp(
    madge: MadgeFn,
    projectRoot: string,
    baseOptions: MadgeOptions,
    userExcludeRegExp: string[],
): Promise<void> {
    if (userExcludeRegExp.length === 0) return;
    const universe = Object.keys((await madge(projectRoot, baseOptions)).obj());
    for (const pattern of userExcludeRegExp) {
        let re: RegExp;
        // eslint-disable-next-line @webpieces/no-unmanaged-exceptions -- invalid user regex is reported, not thrown
        try {
            re = new RegExp(pattern);
        // webpieces-disable catch-error-pattern -- surface the bad pattern to the consumer, then move on
        } catch (err: unknown) {
            const error = toError(err);
            console.warn(
                `⚠️  no-file-import-cycles: excludeRegExp entry "${pattern}" is not a valid regex` +
                    ` (${error.message}) — skipping.`,
            );
            continue;
        }
        if (!universe.some((id: string) => re.test(id))) {
            console.warn(
                `⚠️  no-file-import-cycles: excludeRegExp entry "${pattern}" matched none of the` +
                    ` ${universe.length} traversed file(s) — the exclusion does nothing.` +
                    `\n   Patterns match paths RELATIVE TO THE PROJECT (e.g. "^src/generated/"),` +
                    ` not workspace-rooted or absolute.`,
            );
        }
    }
}

function reportCycles(projectName: string, cycles: string[][]): void {
    console.error(`\n❌ Found ${cycles.length} circular import cycle(s) in ${projectName}:\n`);
    cycles.forEach((cycle: string[], i: number) => {
        console.error(`  ${i + 1}. ${cycle.join(' → ')} → ${cycle[0]}`);
    });
    console.error('\nTo fix, break the cycle (extract a shared module, or use an interface).');
    console.error('To exempt a path (generated code, a deliberate bidirectional model), add a pattern to');
    console.error(`"${RULE_NAME}".excludeRegExp in webpieces.config.json. Patterns match paths RELATIVE`);
    console.error('TO THE PROJECT, e.g. "^src/generated/" or "^src/modules/(item|category)/".');
    console.error('To time-box a known cycle, a human can set "turnOffRuleUntilEpoch"');
    console.error(`(epoch seconds) on the "${RULE_NAME}" rule in webpieces.config.json,`);
    console.error(`or "turnOffRuleWhileOnBranch" (a branch name) to shelter one refactor branch.`);
    console.error(`To turn the gate off entirely, set "${RULE_NAME}".mode to "OFF".\n`);
}

export default async function runExecutor(
    _options: ValidateNoFileImportCyclesOptions,
    context: ExecutorContext,
): Promise<ExecutorResult> {
    const shared = loadAndValidate(context.root).resolved;
    const rule = shared.rules.get(RULE_NAME);

    if (rule && rule.isOff) {
        console.log(`\n⏭️  Skipping ${RULE_NAME} (mode: OFF)\n`);
        return { success: true };
    }

    const projectName = context.projectName ?? 'project';
    const projectConfig = context.projectsConfigurations?.projects[projectName];
    const projectRoot = projectConfig ? path.join(context.root, projectConfig.root) : context.root;

    const epoch = rule?.options['turnOffRuleUntilEpoch'] as number | undefined;
    // turnOffRuleWhileOnBranch is required-but-NULLABLE in the config (null = "always on"), so narrow
    // to a branch NAME here; anything else means no branch scoping.
    const rawBranch = rule?.options['turnOffRuleWhileOnBranch'];
    const branch = typeof rawBranch === 'string' ? rawBranch : undefined;
    const ignoreTypeOnly = (rule?.options['ignoreTypeOnly'] as boolean | undefined) ?? false;
    const excludePackages = (rule?.options['excludePackages'] as string[] | undefined) ?? [];
    const userExcludeRegExp = (rule?.options['excludeRegExp'] as string[] | undefined) ?? [];

    console.log(`\n🔁 Checking import cycles in ${projectName} (madge)\n`);

    const madge = loadMadge();
    // baseOptions carries the built-in + excludePackages regexes only; the real run appends the
    // consumer's excludeRegExp, and the warning pass re-uses baseOptions to see what those patterns
    // *would* have to match against (madge's un-user-filtered id universe).
    const baseOptions = buildMadgeOptions(ignoreTypeOnly, excludePackages, context.root, projectRoot);
    const options = buildMadgeOptions(ignoreTypeOnly, excludePackages, context.root, projectRoot, userExcludeRegExp);
    const result = await madge(projectRoot, options);
    const cycles = result.circular();

    await warnOnUnmatchedExcludeRegExp(madge, projectRoot, baseOptions, userExcludeRegExp);

    if (cycles.length === 0) {
        console.log('✅ No circular import cycles found\n');
        return { success: true };
    }

    reportCycles(projectName, cycles);

    // Grace window or branch hatch active → report but pass; otherwise fail.
    return { success: !isFailingActive(epoch, branch) };
}
